import React, { useEffect, useState } from 'react'
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { fetchFixes, submitFixes } from '../reducers/snp500Slice'

function SymbolFixModal({ unresolved, onSuccess }) {
  const dispatch = useDispatch()
  const [decisions, setDecisions] = useState(() =>
    Object.fromEntries(
      unresolved.map(({ symbol, suggestion }) => [
        symbol,
        { action: 'accept', editValue: suggestion },
      ])
    )
  )
  const [submitted, setSubmitted] = useState(false)
  const [succeeded, setSucceeded] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  useEffect(() => {
    if (unresolved.length > 0) {
      setDecisions(
        Object.fromEntries(
          unresolved.map(({ symbol, suggestion }) => [
            symbol,
            { action: 'accept', editValue: suggestion },
          ])
        )
      )
      setSubmitted(false)
      setSucceeded(false)
    }
  }, [unresolved])

  const show = unresolved.length > 0 && !submitted

  function setAction(symbol, action) {
    setDecisions((prev) => ({ ...prev, [symbol]: { ...prev[symbol], action } }))
  }

  function setEditValue(symbol, value) {
    setDecisions((prev) => ({ ...prev, [symbol]: { ...prev[symbol], editValue: value } }))
  }

  async function handleSubmit() {
    const fixes = unresolved
      .filter(({ symbol }) => decisions[symbol].action !== 'skip')
      .map(({ symbol, suggestion }) => {
        const decision = decisions[symbol]
        const isEdit = decision.action === 'edit'
        return {
          original: symbol,
          replacement: isEdit ? decision.editValue : suggestion,
          source: isEdit ? 'manual' : 'agent',
        }
      })

    try {
      await dispatch(submitFixes(fixes)).unwrap()
      setSubmitted(true)
      setSucceeded(true)
      setSubmitError(null)
      onSuccess()
      dispatch(fetchFixes())
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit fixes. Please try again.')
    }
  }

  return (
    <>
      <Modal show={show} animation={false}>
        <Modal.Header>
          <Modal.Title>Unresolved Symbols</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Suggestion</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {unresolved.map(({ symbol, suggestion }) => {
                const decision = decisions[symbol] ?? { action: 'accept', editValue: suggestion }
                return (
                  <tr key={symbol}>
                    <td>{symbol}</td>
                    <td>
                      {decision.action === 'edit' ? (
                        <Form.Control
                          type="text"
                          value={decision.editValue}
                          onChange={(e) => setEditValue(symbol, e.target.value)}
                        />
                      ) : (
                        suggestion
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant={decision.action === 'accept' ? 'primary' : 'outline-primary'}
                        onClick={() => setAction(symbol, 'accept')}
                      >
                        Accept
                      </Button>{' '}
                      <Button
                        size="sm"
                        variant={decision.action === 'edit' ? 'primary' : 'outline-secondary'}
                        onClick={() => setAction(symbol, 'edit')}
                      >
                        Edit
                      </Button>{' '}
                      <Button
                        size="sm"
                        variant={decision.action === 'skip' ? 'primary' : 'outline-danger'}
                        onClick={() => setAction(symbol, 'skip')}
                      >
                        Skip
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer className="flex-column align-items-stretch gap-2">
          {submitError && (
            <Alert variant="danger" className="mb-0">
              {submitError}
            </Alert>
          )}
          <div className="d-flex justify-content-end">
            <Button variant="primary" onClick={handleSubmit}>
              Submit
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {succeeded && (
        <Alert variant="success" role="alert">
          Symbols updated successfully.
        </Alert>
      )}
    </>
  )
}

export default SymbolFixModal
